<template>
  <div>
    <div class="header-section">
      <div class="content-section">
        <h3 class="section-title text-right">
          {{ $t("excelUpload")  }}
        </h3>

        <!-- Tab Navigation -->
        <div class="flex border-b border-[#EEEEEE] mb-6">
          <button
            :class="['px-4 py-2 text-sm font-medium focus:outline-none', activeTab === 'upload' ? 'border-b-2 border-[#6B7AFF] text-[#6B7AFF]' : 'text-[#808080]']"
            @click="activeTab = 'upload'"
          >
            {{ $t('uploadTab') }}
          </button>
          <button
            :class="['px-4 py-2 text-sm font-medium focus:outline-none', activeTab === 'settings' ? 'border-b-2 border-[#6B7AFF] text-[#6B7AFF]' : 'text-[#808080]']"
            @click="activeTab = 'settings'"
          >
            {{ $t('settingsAndHistoryTab')  }}
          </button>
        </div>

        <div class="content">
          <!-- Upload Tab -->
          <div v-if="activeTab === 'upload'">
            <!-- Sample File Download Link - Only show when no file uploaded -->
            <div v-if="!uploadedData.length" class="bg-[#F2F8FF] p-4 rounded-lg text-right mb-4 flex items-center gap-2">
              <p class="font-bold text-sm">{{ $t("sampleExcelFile") }}</p>
              <p class="text-sm">{{ $t("downloadSampleExcel")  }}</p>
              <a :href="baseUrl + '/lib/sample-excel/trackingcode.xlsx'" 
                 class="inline-flex items-center gap-2 py-2 px-4 bg-[#6B7AFF] hover:bg-[#5A69EE] text-white rounded-lg text-sm mr-auto"
                 download>
                <i class="icon-smsir-download text-[14px]"></i>
                {{ $t("downloadSample") }}
              </a>
            </div>
            
            <!-- Upload Section -->
            <div class="mb-5 p-4 border border-[#CCCCCC] rounded-[15px]" 
                 :class="{ 'mb-0': !uploadedData.length }">
              <div v-if="!uploadedData.length" 
                   class="upload-container" 
                   @click="$refs.fileInput.click()"
                   @dragover.prevent="isDragging = true"
                   @dragleave.prevent="isDragging = false"
                   @drop.prevent="handleFileDrop"
                   :class="{ 'dragging': isDragging }">
                <div class="text-center flex flex-col items-center justify-center p-10">
                  <div class="upload-icon mb-4">
                    <img :src="baseUrl + '/assets/img/upload.svg'" width="40" height="40" alt="Upload" />
                  </div>
                  <p class="mb-2">{{ $t("uploadInstructions") }}</p>
                  <p class="text-sm text-gray-500 mb-4">{{ $t("fileFormatInfo") || "File format should be xlsx or csv or txt." }}</p>
                  
                  <input 
                    type="file" 
                    ref="fileInput" 
                    class="hidden" 
                    accept=".xlsx, .xls, .csv, .txt"
                    @change="handleFileSelect"
                  >
                </div>
              </div>
              
              <div v-else class="flex justify-between items-center">
                <div v-if="selectedFile" class="flex items-center gap-3 text-right">
                  <img :src="baseUrl + '/assets/img/excel.svg'" width="48" height="48" alt="Excel" />
                  <div>
                    <div class="text-base text-[#0D0D0D]">{{ selectedFile.name.split('.').slice(0, -1).join('.')}}</div>
                    <div class="text-sm text-[#808080]">{{ selectedFile.name.split('.').pop() }}. {{ formatFileSize(selectedFile.size) }}</div>
                  </div>
                </div>
                <div class="flex items-center gap-2 text-left">
                  <input 
                    type="file" 
                    ref="fileInput" 
                    class="hidden" 
                    accept=".xlsx, .xls, .csv, .txt"
                    @change="handleFileSelect"
                  >
                  
                  <button 
                    @click="$refs.fileInput.click()" 
                    class="py-2 px-4 bg-[#F2F2F2] hover:bg-[#E5E5E5] rounded-full text-[#0D0D0D] text-sm"
                  >
                    {{ $t("uploadAgain")}}
                  </button>
                  
                  <button 
                    @click="sendSMS" 
                    class="py-2 px-6 bg-[#FFBF00] hover:bg-[#E0A800] rounded-full text-[#0D0D0D] text-sm"
                  >
                    {{ $t("sendMessage")  }}
                  </button>
                </div>
              </div>
            </div>
            
            <!-- Loading state -->
            <div v-if="isUploading" class="bg-white rounded-[15px] p-8 border border-[#D9D9D9] text-center">
              <div class="spinner mb-3"></div>
              <p>{{ $t("processingFile") }}</p>
            </div>
            
            <!-- Error state -->
            <div v-else-if="processingError && !uploadedData.length" class="bg-white rounded-[15px] p-8 border border-[#D9D9D9] text-center">
              <i class="icon-smsir-error text-[40px] text-red-500"></i>
              <p class="mt-2 text-red-500">{{ processingError }}</p>
              
              <!-- Additional guidance for errors -->
              <div v-if="processingError.includes('Spout')" class="mt-4 p-4 bg-gray-100 rounded-lg text-right">
                <p class="font-bold mb-2">{{ $t("suggestedSolutions") || "Suggested Solutions:" }}</p>
                <ol class="list-decimal list-inside text-sm">
                  <li class="mb-1">{{ $t("convertToCSV") || "Convert the file to CSV and try again" }}</li>
                  <li class="mb-1">{{ $t("checkExcelStructure") || "Ensure correct Excel structure" }}</li>
                  <li>{{ $t("contactSupport") || "Contact support if the problem persists" }}</li>
                </ol>
              </div>
            </div>
            
            <!-- Results Section - Only show after file upload -->
            <div v-else-if="uploadedData.length" class="bg-white rounded-[15px] p-0 border border-[#D9D9D9] overflow-hidden">
              <!-- Headers -->
              <div class="grid grid-cols-4 gap-2 p-4 border-b border-[#EEEEEE] text-sm">
                <div class="text-right flex items-center gap-1">
                  <span>{{ $t("orderNumber") || "Order Number" }}</span>
                  <i class="icon-smsir-down text-[12px] transform rotate-180"></i>
                </div>
                <div class="text-right flex items-center gap-1">
                  <span>{{ $t("userName") || "User Name" }}</span>
                  <i class="icon-smsir-down text-[12px] transform rotate-180"></i>
                </div>
                <div class="text-right flex items-center gap-1">
                  <span>{{ $t("trackingCode") || "Tracking Code" }}</span>
                  <i class="icon-smsir-down text-[12px] transform rotate-180"></i>
                </div>
                <div class="text-right flex items-center gap-1">
                  <span>{{ $t("mobileNumber") || "Mobile Number" }}</span>
                  <i class="icon-smsir-down text-[12px] transform rotate-180"></i>
                </div>
              </div>
              
              <!-- Data rows -->
              <div>
                <div v-for="(user, index) in uploadedData" :key="index" 
                  class="grid grid-cols-4 gap-2 p-4 border-b border-[#EEEEEE] hover:bg-[#F9F9F9] text-sm"
                  :class="{'bg-[#FFF5F5] hover:bg-[#FFECEC]': user.status === 'error'}">
                  <div class="text-right font-medium">{{ user.orderId }}</div>
                  <div class="text-right font-medium" :class="{'text-red-500': user.status === 'error'}">{{ user.username }}</div>
                  <div class="text-right text-[#696969]">{{ user.trackingCode }}</div>
                  <div class="text-right text-[#696969] font-[IRANSans]">{{ user.phoneNumber }}</div>
                </div>
                
                <!-- No data message -->
                <div v-if="uploadedData.length === 0" class="p-6 text-center text-gray-500">
                  {{ $t("noDataFound") || "No data found." }}
                </div>
              </div>
            </div>
          </div>

          <!-- Settings & History Tab -->
          <div v-if="activeTab === 'settings'">
            <!-- Template ID Setting -->
            <div class="bg-white  text-right mb-4">
              <label for="template_id" class="block text-sm font-medium mb-2">{{ $t("trackingTemplateId") }}</label>
              <div class="flex items-center gap-2">
             
                  <input 
                    type="text" 
                    id="template_id" 
                    v-model="templateId" 
                    class="w-10/12 border border-[#D9D9D9] rounded-lg px-3 py-2 text-base"
                    :placeholder="$t('trackingTemplateId')"
                  />
                  <button 
                  @click="saveTemplateId" 
                  class="px-3 py-2 bg-[#6B7AFF] hover:bg-[#5A69EE] text-white rounded-lg flex items-center gap-2 mb-0 text-base"
                >
                  <i class="icon-smsir-save text-[14px]"></i>
                  {{ $t("saveSettings") }}
                </button>
          
            
              </div>
            </div>
            
            <div v-if="isLoadingHistory" class="text-center py-8">
              <div class="spinner mb-3"></div>
              <p>{{ $t("loadingHistory") || "Loading history..." }}</p>
            </div>
            
            <div v-else-if="uploadHistory.length === 0" class="bg-white rounded-[15px] p-6 border border-[#D9D9D9] text-center">
              <p class="text-gray-500">{{ $t("noHistoryFound") }}</p>
              <button 
                @click="reloadHistory" 
                class="mt-3 py-2 px-4 bg-[#F2F2F2] hover:bg-[#E5E5E5] rounded-full text-[#0D0D0D] text-sm"
              >
                {{ $t("tryAgain") || "Try Again" }}
              </button>
            </div>
            
            <div v-else class="bg-white rounded-[15px] p-0 border border-[#D9D9D9] overflow-hidden">
              <!-- History Headers -->
              <div class="grid grid-cols-6 gap-2 p-4 border-b border-[#EEEEEE] text-sm">
                <div class="text-right">{{ $t("date") }}</div>
                <div class="text-right">{{ $t("file")}}</div>
                <div class="text-right">{{ $t("fileType")}}</div>
                <div class="text-right">{{ $t("recordCount") }}</div>
                <div class="text-right">{{ $t("status") }}</div>
                <div class="text-right">{{ $t("user")}}</div>
              </div>
              
              <!-- History Rows -->
              <div>
                <div v-for="(history, index) in uploadHistory" :key="history.id" 
                    class="grid grid-cols-6 gap-2 p-4 border-b border-[#EEEEEE] history-item cursor-pointer"
                    @click="showHistoryDetails(history)">
                  <div class="text-right">{{ formatDate(history.date) }}</div>
                  <div class="text-right" :title="history.file_name">
                    <div class="truncate max-w-[150px]">{{ history.file_name || $t("file") }}</div>
                    <div class="text-xs text-gray-500">{{ formatFileSize(history.file_size) }}</div>
                  </div>
                  <div class="text-right">{{ history.file_type.toUpperCase() }}</div>
                  <div class="text-right">{{ history.total_records }} {{ $t("records") || "records" }}</div>
                  <div class="text-right">
                    <span class="text-green-500">{{ history.success_count }} {{ $t("successful") || "successful" }}</span>
                    <span v-if="history.error_count > 0" class="text-red-500 mr-2">{{ history.error_count }} {{ $t("failed") || "failed" }}</span>
                  </div>
                  <div class="text-right">{{ history.user_name }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Confirmation Modal -->
          <div v-if="showConfirmModal" class="modal">
            <div class="modal-content max-w-md">
              <div class="flex justify-between items-center mb-4">
                <h2 class="text-[18px] text-[#0D0D0D]">{{ $t('confirmSendSMS') || 'Confirm SMS Sending' }}</h2>
                <button @click="cancelSendSMS" class="text-2xl">&times;</button>
              </div>
              
              <p class="mb-4 text-[#4D4D4D]">
                {{ $t('confirmSendSMSMessage', {count: uploadedData.length}) || `Are you sure you want to send SMS to ${uploadedData.length} customers?` }}
              </p>
              
              <div class="flex justify-end gap-2">
                <button @click="cancelSendSMS" class="px-4 py-2 border border-gray-300 rounded-full hover:bg-gray-100 text-base">
                  {{ $t('no') || 'No' }}
                </button>
                <button @click="confirmSendSMS" class="px-4 py-2 bg-[#FFBF00] hover:bg-[#E0A800] text-[#0D0D0D] rounded-full flex items-center gap-2 text-base">
                  <i class="icon-smsir-send text-xl"></i>
                  {{ $t('yesSend') || 'Yes, Send' }}
                </button>
              </div>
            </div>
          </div>
          
          <!-- History Details Modal -->
          <div v-if="showHistoryDetailsModal" class="modal">
            <div class="modal-content">
              <div class="flex justify-between items-center mb-4">
                <h2 class="font-semibold text-[#0D0D0D] text-lg">{{ $t("historyDetails") }}</h2>
                <button @click="showHistoryDetailsModal = false" class="text-2xl">&times;</button>
              </div>
              
              <div class="mt-4">
                <div class="mb-2">
                  <h3 class="text-sm text-[#4D4D4D]">{{ $t("date") || "Date" }}</h3>
                  <p class="text-[#0D0D0D] text-base mt-3">{{ formatDate(selectedHistory.date) }}</p>
                  <hr class="border-[#D9D9D9] w-full h-[1px] mt-3">
                </div>
                
                <div class="mb-2">
                  <h3 class="text-sm text-[#4D4D4D]">{{ $t("file") || "File" }}</h3>
                  <p class="text-[#0D0D0D] text-base mt-3">{{ selectedHistory.file_name }}</p>
                  <hr class="border-[#D9D9D9] w-full h-[1px] mt-3">
                </div>
                
                <div class="mb-2">
                  <h3 class="text-sm text-[#4D4D4D]">{{ $t("statistics")  }}</h3>
                  <div class="flex gap-4 mt-3">
                    <p class="text-[#0D0D0D] text-base">{{ $t("totalRecords") }}: {{ selectedHistory.total_records }}</p>
                    <p class="text-green-500 text-base">{{ $t("successful")  }}: {{ selectedHistory.success_count }}</p>
                    <p class="text-red-500 text-base" v-if="selectedHistory.error_count > 0">{{ $t("failed")}}: {{ selectedHistory.error_count }}</p>
                  </div>
                  <hr class="border-[#D9D9D9] w-full h-[1px] mt-3">
                </div>
                
                <div class="mb-2">
                  <h3 class="text-sm text-[#4D4D4D]">{{ $t("sampleData") }}</h3>
                  <div class="overflow-x-auto mt-3">
                    <table class="history-table w-full">
                      <thead>
                        <tr>
                          <th>{{ $t("orderNumber")}}</th>
                          <th>{{ $t("userName")}}</th>
                          <th>{{ $t("trackingCode")}}</th>
                          <th>{{ $t("mobileNumber") }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(item, itemIndex) in selectedHistory.sample_data" :key="itemIndex">
                          <td>{{ item.orderId }}</td>
                          <td>{{ item.username }}</td>
                          <td>{{ item.trackingCode }}</td>
                          <td>{{ item.phoneNumber }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                
                <div class="mt-4 flex justify-end gap-2">
                  <button 
                    @click="showHistoryDetailsModal = false" 
                    class="px-4 py-2 border border-gray-300 rounded-full hover:bg-gray-100 text-base"
                  >
                    {{ $t("close") || "Close" }}
                  </button>
                  <button 
                    @click="reuseHistoryData()" 
                    class="px-4 py-2 bg-[#F2F2F2] hover:bg-[#E5E5E5] rounded-full text-[#0D0D0D] text-base flex items-center gap-2"
                  >
                    <i class="icon-smsir-redo text-xl"></i>
                    {{ $t("reuseData")}}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import axios from 'axios';

export default {
  props: {
    settings: {
      type: Object,
      required: true,
    },
    active: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isDragging: false,
      selectedFile: null,
      isUploading: false,
      isProcessed: false,
      uploadedData: [],
      processingError: null,
      uploadHistory: [],
      isLoadingHistory: false,
      expandedHistoryIndex: null,
      showConfirmModal: false,
      dataToSend: null,
      showHistoryDetailsModal: false,
      selectedHistory: null,
      templateId: '',
      activeTab: 'upload'
    };
  },
  computed: {
    baseUrl() {
      // Use settings.plugin_url if available, otherwise construct a fallback URL
      if (this.settings && this.settings.pluginUrl) {
        return this.settings.pluginUrl;
      }
      
      // Fallback to WP site URL (if window.location is available)
      const siteUrl = window.location.origin || '';
      const adminPath = window.location.pathname.includes('/wp-admin/') 
        ? '/wp-content/plugins/smsir' 
        : '';
        
      return siteUrl + adminPath;
    }
  },
  watch: {
    active(newVal) {
      if (newVal) {
        this.loadUploadHistory();
      }
    }
  },
  mounted() {
    this.loadUploadHistory();
    this.loadTemplateId();
  },
  methods: {
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (this.validateFile(file)) {
        this.selectedFile = file;
        this.processFile(file);
      }
    },
    handleFileDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      if (this.validateFile(file)) {
        this.selectedFile = file;
        this.processFile(file);
      }
    },
    validateFile(file) {
      if (!file) return false;
      
      const fileName = file.name.toLowerCase();
      if (!fileName.endsWith('.xlsx') && 
          !fileName.endsWith('.xls') && 
          !fileName.endsWith('.csv') && 
          !fileName.endsWith('.txt')) {
        Swal.fire({
          text: this.$t('invalidFileType') || 'Please upload a file with a valid format',
          icon: 'error',
          toast: true,
          position: 'top-start',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: { popup: 'smsir-swal' }
        });
        return false;
      }
      
      return true;
    },
    processFile(file) {
      this.isUploading = true;
      this.processingError = null;
      this.uploadedData = [];
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('action', 'smsir_process_excel_file');
      
      axios.post(this.settings.ajax_url || ajaxurl, formData)
        .then(response => {
          this.isUploading = false;
          
          if (response.data.success && response.data.data) {
            this.isProcessed = true;
            
            // Convert western numbers to Persian if needed
            this.uploadedData = response.data.data.map(item => {
              return {
                orderId: this.convertToPersianNumbers(item.orderId || ''),
                username: item.username || '',
                trackingCode: this.convertToPersianNumbers(item.trackingCode || ''),
                phoneNumber: this.convertToPersianNumbers(item.phoneNumber || ''),
                status: item.status || 'success'
              };
            });
            
            // Refresh history after successful upload
            this.loadUploadHistory();
          } else {
            this.processingError = response.data.data && response.data.data.message ? 
              response.data.data.message : this.$t('fileProcessingError') || 'Error processing file';
            
            Swal.fire({
              text: this.processingError,
              icon: 'error',
              toast: true,
              position: 'top-start',
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
              customClass: { popup: 'smsir-swal' }
            });
          }
        })
        .catch(error => {
          this.isUploading = false;
          this.processingError = error.response?.data?.message || this.$t('serverConnectionError') || 'Error connecting to server';
          
          Swal.fire({
            text: this.processingError,
            icon: 'error',
            toast: true,
            position: 'top-start',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            customClass: { popup: 'smsir-swal' }
          });
        });
    },
    loadUploadHistory() {
      this.isLoadingHistory = true;
      
      const formData = new FormData();
      formData.append('action', 'smsir_get_upload_history');
      
      // Try to load history from server
      axios.post(this.settings.ajax_url || ajaxurl, formData)
        .then(response => {
          this.isLoadingHistory = false;
          
          if (response.data && response.data.success && Array.isArray(response.data.data) && response.data.data.length > 0) {
            this.uploadHistory = response.data.data;
          }
        })
        .catch(error => {
          this.isLoadingHistory = false;
       
        });
    },
    
   
    toggleHistoryDetails(index) {
      if (this.expandedHistoryIndex === index) {
        this.expandedHistoryIndex = null;
      } else {
        this.expandedHistoryIndex = index;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      
      const date = new Date(dateStr);
      return new Intl.DateTimeFormat('fa-IR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    },
    convertToPersianNumbers(text) {
      if (!text) return '';
      
      const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
      return String(text).replace(/[0-9]/g, function(d) {
        return persianDigits[parseInt(d)];
      });
    },
    formatFileSize(size) {
      if (size < 1024) {
        return size + ' B';
      } else if (size < 1024 * 1024) {
        return (size / 1024).toFixed(1) + ' KB';
      } else {
        return (size / (1024 * 1024)).toFixed(1) + ' MB';
      }
    },
    sendSMS() {
      if (!this.uploadedData.length) {
        Swal.fire({
          text: this.$t('pleaseUploadFile') || 'Please upload a file first',
          icon: 'warning',
          toast: true,
          position: 'top-start',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: { popup: 'smsir-swal' }
        });
        return;
      }
      
      // Prepare data for sending
      this.dataToSend = this.uploadedData.map(item => {
        // Convert Persian numbers to standard numbers
        let phone = item.phoneNumber.replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d));
        
        // Format phone number: if starts with 989, replace with 09
        if (phone.startsWith('989')) {
          phone = '0' + phone.substring(2);
        }
        
        return {
          orderId: item.orderId.replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d)),
          trackingCode: item.trackingCode.replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d)),
          phoneNumber: phone
        };
      });
      
      // Show confirmation modal
      this.showConfirmModal = true;
    },
    
    confirmSendSMS() {
      this.showConfirmModal = false;
      
      if (!this.dataToSend || !this.dataToSend.length) {
        return;
      }
      
      const formData = new FormData();
      formData.append('action', 'smsir_send_bulk_tracking_codes');
      formData.append('data', JSON.stringify(this.dataToSend));
      
      // Send request to server
      axios.post(this.settings.ajax_url || ajaxurl, formData)
        .then(response => {
          if (response.data && response.data.success) {
            Swal.fire({
              text: this.$t('smsSendingInitiated') || 'Messages sent successfully',
              icon: 'success',
              toast: true,
              position: 'top-start',
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
              customClass: { popup: 'smsir-swal' }
            });
          } else {
            Swal.fire({
              text: response.data?.data?.message || this.$t('errorSendingSMS') || 'Error sending SMS',
              icon: 'error',
              toast: true,
              position: 'top-start',
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
              customClass: { popup: 'smsir-swal' }
            });
          }
        })
        .catch(error => {
          console.error('Error sending SMS:', error);
          Swal.fire({
            text: error.response?.data?.message || this.$t('serverConnectionError') || 'Error connecting to server',
            icon: 'error',
            toast: true,
            position: 'top-start',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            customClass: { popup: 'smsir-swal' }
          });
        });
    },
    
    cancelSendSMS() {
      this.showConfirmModal = false;
      this.dataToSend = null;
    },
    
    /**
     * Reuse data from a previous upload
     */
    reusePreviousData(historyData, historyId) {
      // Check if there is data to reuse
      if (!historyData || !historyData.length) {
        Swal.fire({
          text: this.$t('noDataToReuse') || 'No data available to reuse',
          icon: 'warning',
          toast: true,
          position: 'top-start',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: { popup: 'smsir-swal' }
        });
        return;
      }
      
      // Confirm reuse
      Swal.fire({
        title: this.$t('reuseData') || 'Reuse Data',
        text: this.$t('confirmReuseData') || 'Are you sure you want to use this data?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('yes') || 'Yes',
        cancelButtonText: this.$t('no') || 'No',
        customClass: { popup: 'smsir-swal' }
      }).then((result) => {
        if (result.isConfirmed) {
          if (historyId && historyId !== 'sample1') {
            // First try to get full data for this history entry (if it's not a demo ID)
            this.loadHistoryData(historyId);
          } else {
            // Use the sample data directly
            this.setHistoryDataAsActive(historyData);
          }
        }
      });
    },
    
    /**
     * Load the full data for a specific history entry
     */
    loadHistoryData(historyId) {
      const formData = new FormData();
      formData.append('action', 'smsir_get_history_data');
      formData.append('history_id', historyId);
      
      axios.post(this.settings.ajax_url || ajaxurl, formData)
        .then(response => {
          if (response.data && response.data.success && response.data.data) {
            this.setHistoryDataAsActive(response.data.data);
          } else {
            // Fallback to using sample data
            const historyEntry = this.uploadHistory.find(h => h.id === historyId);
            if (historyEntry && historyEntry.sample_data) {
              this.setHistoryDataAsActive(historyEntry.sample_data);
            } else {
              // Show error notification
              Swal.fire({
                text: 'خطا در بارگذاری داده‌های قبلی',
                icon: 'error',
                toast: true,
                position: 'top-start',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
                customClass: { popup: 'smsir-swal' }
              });
            }
          }
        })
        .catch(error => {
          console.error('Error loading history data:', error);
          
          // Fallback to using sample data
          const historyEntry = this.uploadHistory.find(h => h.id === historyId);
          if (historyEntry && historyEntry.sample_data) {
            this.setHistoryDataAsActive(historyEntry.sample_data);
          } else {
            Swal.fire({
              text: 'خطا در بارگذاری داده‌های قبلی',
              icon: 'error',
              toast: true,
              position: 'top-start',
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
              customClass: { popup: 'smsir-swal' }
            });
          }
        });
    },
    
    /**
     * Set history data as the active data
     */
    setHistoryDataAsActive(historyData) {
      // Set the data
      this.uploadedData = historyData.map(item => {
        return {
          orderId: this.convertToPersianNumbers(item.orderId || ''),
          username: item.username || '',
          trackingCode: this.convertToPersianNumbers(item.trackingCode || ''),
          phoneNumber: this.convertToPersianNumbers(item.phoneNumber || ''),
          status: item.status || 'success'
        };
      });
      
      // Close history details
      this.expandedHistoryIndex = null;
      
      // Notify user
      Swal.fire({
        text: this.$t('previousDataLoaded') || 'Previous data loaded successfully',
        icon: 'success',
        toast: true,
        position: 'top-start',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: { popup: 'smsir-swal' }
      });
    },
    reloadHistory() {
      this.loadUploadHistory();
    },
    toggleHistoryDetails(index) {
      if (this.expandedHistoryIndex === index) {
        this.expandedHistoryIndex = null;
      } else {
        this.expandedHistoryIndex = index;
      }
    },
    showHistoryDetails(history) {
      this.selectedHistory = history;
      this.showHistoryDetailsModal = true;
    },
    reuseHistoryData() {
      if (!this.selectedHistory || !this.selectedHistory.sample_data || !this.selectedHistory.sample_data.length) {
        Swal.fire({
          text: this.$t('noDataToReuse') || 'No data available to reuse',
          icon: 'warning',
          toast: true,
          position: 'top-start',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: { popup: 'smsir-swal' }
        });
        this.showHistoryDetailsModal = false;
        return;
      }
      
      this.showHistoryDetailsModal = false;
      
      // Check if we need to load full data or can use sample data
      if (this.selectedHistory.id && this.selectedHistory.id !== 'sample1') {
        // Get full data for this history entry
        this.loadHistoryData(this.selectedHistory.id);
      } else {
        // Use the sample data directly
        this.setHistoryDataAsActive(this.selectedHistory.sample_data);
      }
    },
    saveTemplateId() {
      const formData = new FormData();
      formData.append('action', 'smsir_save_tracking_template_id');
      formData.append('template_id', this.templateId);
      
      axios.post(this.settings.ajax_url || ajaxurl, formData)
        .then(response => {
          if (response.data && response.data.success) {
            Swal.fire({
              text: this.$t('templateIdSaved') || 'Template ID saved successfully',
              icon: 'success',
              toast: true,
              position: 'top-start',
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
              customClass: { popup: 'smsir-swal' }
            });
          } else {
            Swal.fire({
              text: response.data?.data?.message || this.$t('errorSavingTemplateId') || 'Error saving template ID',
              icon: 'error',
              toast: true,
              position: 'top-start',
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
              customClass: { popup: 'smsir-swal' }
            });
          }
        })
        .catch(error => {
          console.error('Error saving template ID:', error);
          Swal.fire({
            text: error.response?.data?.message || this.$t('serverConnectionError') || 'Error connecting to server',
            icon: 'error',
            toast: true,
            position: 'top-start',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            customClass: { popup: 'smsir-swal' }
          });
        });
    },
    loadTemplateId() {
      const formData = new FormData();
      formData.append('action', 'smsir_get_tracking_template_id');
      
      axios.post(this.settings.ajax_url || ajaxurl, formData)
        .then(response => {
          if (response.data && response.data.success) {
            this.templateId = response.data.data || '';
          }
        })
        .catch(error => {
          console.error('Error loading template ID:', error);
        });
    }
  }
};
</script>

<style scoped>
.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
}

.upload-container {
  min-height: 200px;
  border: 2px dashed #99B3FF;
  border-radius: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.upload-container:hover {
  border-color: #6B7AFF;
  background-color: rgba(107, 122, 255, 0.05);
}

.upload-container.dragging {
  border-color: #6B7AFF;
  background-color: rgba(107, 122, 255, 0.1);
  transform: scale(1.01);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
}

.spinner {
  display: inline-block;
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: #6B7AFF;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.smsir-swal {
  top: 50px !important;
  background-color: #fff !important;
  box-shadow: 0 0 1px rgba(0, 0, 0, .075), 0 1px 2px rgba(0, 0, 0, .075), 1px 2px 4px rgba(0, 0, 0, .075), 1px 3px 8px rgba(0, 0, 0, .075), 2px 4px 16px rgba(0, 0, 0, .075) !important;
}

/* Upload history styling */
.history-header {
  font-size: 1.125rem;
  font-weight: 500;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #EEEEEE;
  margin-bottom: 1rem;
}

.history-item {
  transition: all 0.2s ease;
}

.history-item:hover {
  background-color: #F5F7FF;
}

.history-details {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;
}

.history-details.expanded {
  max-height: 500px;
}

.history-table {
  width: 100%;
  border-collapse: collapse;
}

.history-table th, 
.history-table td {
  padding: 0.5rem 0.75rem;
  text-align: right;
  border-bottom: 1px solid #EEEEEE;
}

.history-table th {
  background-color: #F5F7FF;
  font-weight: 500;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 24px;
  border-radius: 30px;
  width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

@media (min-width: 768px) {
  .modal-content {
    width: 600px;
  }
}
</style> 